import { PageLayout } from '@/components/feature/PageLayout';
import { useFadeUp } from '@/hooks/useFadeUp';
import { Wrench, MapPin, Shield, Clock } from 'lucide-react';

const seo = {
  title: 'Qui sommes-nous — Artisan spécialiste volets roulants Charleroi',
  description: 'Artisan local spécialisé dans la réparation et le dépannage de volets roulants à Charleroi et dans le Hainaut. Découvrez notre histoire, nos valeurs et notre engagement qualité. Garantie 10 ans.',
  keywords: 'artisan volet roulant Charleroi, réparateur volet Hainaut, spécialiste volet roulant Belgique, entreprise volet Charleroi',
  canonicalUrl: '/qui-sommes-nous/',
};

function Hero() {
  return (
    <section className="relative min-h-[350px] md:min-h-[420px] flex items-center overflow-hidden">
      <img
        src="/images/hero-qui-sommes-nous.webp"
        alt="Artisan spécialiste volets roulants à Charleroi"
        className="absolute inset-0 w-full h-full object-cover object-top"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/85 to-secondary-500/70" />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">
            Qui sommes-nous
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/85">
            Un artisan local, spécialiste de la réparation de volets roulants à Charleroi
          </p>
        </div>
      </div>
    </section>
  );
}

export default function QuiSommesNousPage() {
  const ref1 = useFadeUp();
  const ref2 = useFadeUp();

  return (
    <PageLayout seo={seo}>
      <Hero />

      {/* Section Histoire */}
      <section className="py-14 md:py-20 bg-background-50">
        <div ref={ref1} className="fade-up max-w-4xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-700 mb-5">
                Artisan local à Charleroi, spécialiste des volets roulants
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  Basés à <strong>Charleroi (6000)</strong>, nous sommes une entreprise artisanale spécialisée dans la <strong>réparation</strong>, le <strong>dépannage</strong> et la <strong>motorisation</strong> de volets roulants. Nous intervenons dans tout le <strong>Hainaut</strong>.
                </p>
                <p>
                  Notre philosophie est simple : <strong>réparer plutôt que remplacer</strong>. Nous privilégions toujours la solution la plus économique et durable pour nos clients. Un diagnostic précis, un devis clair, et une intervention soignée.
                </p>
                <p>
                  Contrairement à beaucoup, nous ne cherchons pas à vous vendre un nouveau volet roulant à tout prix. Si votre volet est réparable — et c'est le cas dans la grande majorité des situations — nous le réparons.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-80 lg:h-96">
              <img
                src="/images/qui-sommes-nous-artisan.webp"
                alt="Artisan volet roulant Charleroi - Portrait professionnel"
                title="Artisan spécialiste volets roulants Charleroi"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nos engagements */}
      <section className="py-14 md:py-20 bg-background-100">
        <div ref={ref2} className="fade-up max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-700 text-center mb-10">
            Nos engagements
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { icon: Wrench, title: 'Réparation avant remplacement', desc: 'Nous analysons la panne et réparons chaque fois que c\'est possible. Économique et écologique.' },
              { icon: MapPin, title: 'Intervention locale', desc: 'Basés à Charleroi, nous connaissons parfaitement la région et intervenons rapidement dans tout le Hainaut.' },
              { icon: Shield, title: 'Garantie 10 ans', desc: 'Toutes nos réparations et installations sont couvertes par une garantie de 10 ans, pièces et main-d\'œuvre incluses.' },
              { icon: Clock, title: 'Disponibilité 7j/7', desc: 'Nous savons qu\'un volet bloqué est urgent. Nous intervenons rapidement, y compris le week-end pour les urgences.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="card border border-background-200">
                  <div className="w-11 h-11 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent-500" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-700 text-base mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}